import {
  CreateConsultationInput,
  useCreateConsultationMutation,
  Gender,
  ConsultationTarget,
  useSymptomsQuery,
  Symptom,
  useMedicalHistoriesQuery,
} from '@/graphql/types'
import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Modal } from '../../shared/components/Modal'
import { useAuth } from './AuthProvider'
import {
  Label,
  Button,
  Input,
  Textarea,
  CheckBox,
  ErrorMessage,
  Select,
  Card,
} from './Basics'
import { Confirm } from './Confirm'

const INITIAL_SELECTED_AGE = 50

export const Consultation: React.FC = () => {
  const { loading, isLoggedIn } = useAuth()
  const [selectedSymptomIds, setSelectedSymptomIds] = useState<string[]>([])
  const [input, setInput] = useState<CreateConsultationInput>({
    age: INITIAL_SELECTED_AGE,
    title: '',
    body: '',
    gender: undefined,
    targetType: ConsultationTarget.Myself,
    symptomIds: selectedSymptomIds,
    historyId: '',
  })
  const [agree, setAgree] = useState<boolean>(false)
  const [confirm, setConfirm] = useState<boolean>(false)
  const [submittable, setSubmittable] = useState<boolean>(false)
  const [modal, setModal] = useState<boolean>(false)
  const [mutate, { error }] = useCreateConsultationMutation()
  const history = useHistory()
  const symptoms = useSymptomsQuery()
  const medicalHistory = useMedicalHistoriesQuery()

  // 20~100歳
  const ageOptions = [...Array(81)].map((_, i) => 20 + i)

  const symptomCheckboxes = (option: Symptom) => {
    const target = symptoms.data?.symptoms?.nodes?.find(
      (e) => e?.id === option.id
    )
    const checked = selectedSymptomIds?.includes(target!.id)
    const last = symptoms.data?.symptoms?.nodes?.slice(-1)[0]

    return (
      <div key={option.id}>
        <CheckBox
          className="form-checkbox"
          id={option.id}
          onChange={(e) => {
            e.persist()
            if (e.target.checked) {
              setSelectedSymptomIds((o) => [...o, e.target.value])
            } else {
              setSelectedSymptomIds((o) =>
                o.filter((id) => id !== e.target.value)
              )
            }
          }}
          checked={checked}
          value={option.id}
        />
        <Label className="text-lg checkbox" htmlFor={option.id}>
          {option.description}
        </Label>
        {last?.id !== option.id && (
          <span className="my-3 block border-b border-gray-50 border-dotted"></span>
        )}
      </div>
    )
  }

  const requiredTag = (
    <span className="text-white font-bold bg-red-600 rounded p-1">必須</span>
  )

  const checkCheckValidation = (str: string): string => {
    if (str.length < 10) {
      return '相談タイトルは10字以上である必要があります。'
    } else if (str.length > 30) {
      return '相談内容は30字以下である必要があります。'
    } else {
      return ''
    }
  }

  const checkBodyValidation = (str: string): string => {
    if (str.length <= 20) {
      return '相談内容は21字以上である必要があります。'
    } else if (str.length > 1000) {
      return '相談内容は1000字未満である必要があります。'
    } else {
      return ''
    }
  }

  const submit = () => {
    mutate({
      variables: {
        input: {
          ...input,
          symptomIds: selectedSymptomIds,
        },
      },
      update(_, result) {
        const id = result.data?.createConsultation?.consultation?.id || ''
        history.push(
          `/features/qa/mild_cognitive_impairment/consultations/${id}`
        )
      },
    })
  }

  useEffect(() => {
    if (!loading && !isLoggedIn) {
      location.href = '/features/qa/mild_cognitive_impairment'
    }
  }, [loading, isLoggedIn])

  useEffect(() => {
    if (
      checkCheckValidation(input.title).length === 0 &&
      checkBodyValidation(input.body).length === 0 &&
      agree &&
      input.age &&
      (input.gender === Gender.Female || input.gender === Gender.Male) &&
      selectedSymptomIds.length !== 0 &&
      input.historyId.length !== 0
    ) {
      setSubmittable(true)
    } else {
      setSubmittable(false)
    }
  }, [agree, input, selectedSymptomIds])

  return (
    <React.Fragment>
      {loading && <div></div>}
      {isLoggedIn && !confirm && (
        <div className="bg-base-light-color pb-20">
          <div className="w-11/12 pc:w-4/12 mx-auto mx-2">
            <div className="flex justify-center mx-auto">
              <div className="text-app-color font-black p-5 text-2xl pc:text-xl">
                相談投稿の入力
              </div>
            </div>
            <div>
              {requiredTag}
              <p className="text-app-color text-lg font-extrabold pb-3 pt-2">
                1.今回のご相談の対象者様はどなたですか？
              </p>
            </div>
            <Card className="pt-5 pb-10">
              <div className="form">
                <Label>対象者</Label>
                <Select
                  className="form-select"
                  value={input.targetType}
                  onChange={(e) => {
                    setInput({
                      ...input,
                      targetType: e.target.value as ConsultationTarget,
                    })
                  }}
                >
                  <option value={ConsultationTarget.Myself}>
                    私自身（相談者本人）
                  </option>
                  <option value={ConsultationTarget.Others}>
                    私以外の方（ ご家族やご友人、ケアに関わる方など）
                  </option>
                </Select>
              </div>
              <p className="text-app-color text-sm font-bold pt-4">
                以下項目は相談対象者の方の情報をご入力ください。
              </p>
              <div className="form mt-4">
                <Label>年齢</Label>
                <Select
                  className="form-select"
                  value={input.age}
                  onChange={(e) => {
                    setInput({ ...input, age: Number(e.target.value) })
                  }}
                >
                  {ageOptions.map((v) => (
                    <option value={v} key={v}>
                      {v}歳
                    </option>
                  ))}
                </Select>
              </div>
              <div className="form mt-4">
                <Label>性別</Label>
                <Select
                  className="form-select"
                  value={input.gender as Gender}
                  onChange={(e) => {
                    setInput({ ...input, gender: e.target.value as Gender })
                  }}
                >
                  <option value={undefined}>選択する</option>
                  <option value={Gender.Male}>男性</option>
                  <option value={Gender.Female}>女性</option>
                </Select>
              </div>
              <div className="form mt-4">
                <Label>
                  今回のご相談の症状の診断・症状状況を教えてください
                  <span className="font-normal">
                    （その他にも、治療中の病気があればご記入ください）
                  </span>
                </Label>
                <Select
                  className="form-select"
                  value={input.historyId}
                  onChange={(e) => {
                    setInput({ ...input, historyId: e.target.value })
                  }}
                >
                  <option value="">選択する</option>
                  {medicalHistory.data?.medicalHistories?.nodes?.map((his) => (
                    <option value={his?.id} key={his?.id}>
                      {his?.body}
                    </option>
                  ))}
                </Select>
              </div>
            </Card>
            <div>
              <div className="pt-12">
                {requiredTag}
                <p className="text-app-color text-lg font-extrabold pb-3 pt-2">
                  2.当てはまる症状はありますか？
                  <br />
                  （当てはまるものをすべて選択してください）
                </p>
              </div>
              <Card>
                {symptoms.data?.symptoms?.nodes?.map((symptom) => {
                  return symptomCheckboxes(symptom!)
                })}
              </Card>
            </div>
            <div className="pt-12">
              <div>
                {requiredTag}
                <p className="text-app-color text-lg font-extrabold pb-3 pt-2">
                  3.相談内容を入力してください
                </p>
              </div>
              <Card>
                <Label>
                  タイトル
                  <span className="text-lg font-normal">（10~30文字）</span>
                </Label>
                <Input
                  id="title"
                  value={input.title}
                  className="py-3 px-4 mb-4"
                  placeholder="タイトルを入力"
                  onChange={(e) => {
                    setInput({ ...input, title: e.target.value })
                  }}
                />
                <p className="text-red-500 text-sm font-bold">
                  {input.title.length !== 0 &&
                    checkCheckValidation(input.title)}
                </p>
                <ErrorMessage error={error} columnName="title" />
                <Label>
                  本文
                  <span className="text-lg font-normal">（21~1000文字）</span>
                </Label>
                <Textarea
                  id="body"
                  value={input.body}
                  className="h-32 py-3 px-4"
                  placeholder="本文を入力"
                  onChange={(e) => {
                    setInput({ ...input, body: e.target.value })
                  }}
                />
                <p className="text-red-500 text-sm font-bold">
                  {input.body.length !== 0 && checkBodyValidation(input.body)}
                </p>
                <ErrorMessage error={error} columnName="title" />
                <div className="pt-1 text-xxs">
                  ※お名前や住所など個人情報の入力はしないようにお願いいたします。
                </div>
              </Card>
              <div className="mt-10 text-xxs text-gray-70">
                <p>【このサービスについて】</p>
                <p className="pl-2">
                  このサービスは株式会社メディカルノートが運営する、オンライン医療相談サービスです。認知症に関するご相談について、医療チームがWebサイト上でご回答をいたします。
                  <br />
                  このサービスの利用については「
                  <a
                    className="text-link-color underline"
                    target="_blank"
                    rel="noreferrer"
                    href="https://medicalnote.jp/terms"
                  >
                    「Medical Note 医療相談サービス利用規約（個人会員規約）」
                  </a>
                  」が適用されますが、サービス利用範囲は以下のとおりとなり、同利用規約の第3条（会員の定義と利用範囲）に優先します。
                </p>

                <p className="pt-2">【本サービスにおける利用範囲】</p>
                <p className="pl-2">
                  ・本サービスにおいて、Medical
                  Note医療チームに対し、認知症に関する医療相談を投稿することができ、またその相談に対する回答を見ることができます。また、回答に対する聞き返しの投稿をすることができます。
                </p>

                <p className="pt-2">【注意事項】</p>
                <p className="pl-2">
                  ・この相談窓口は認知症に関する相談を受け付ける窓口です。この窓口にそぐわない内容の相談であると運営が判断した場合、回答を控えさせていただく場合がございます。
                  <br />
                  ・確定診断を受けている場合、治療をすでに受けている場合は対象外となります。かかりつけ医にご相談ください。
                  <br />
                  ・相談において、お名前や住所などの個人情報の入力はしないようお願いいたします。
                  <br />
                  ・相談に対する回答においては医療に関する情報を提供しますが、診療を行うものではありません。
                  <br />
                  ・サプリメントや市販薬を含め、薬の服用に関するアドバイスはいたしかねます。
                  <br />
                  ・必ず自己の責任においてサービスを利用し、ご自身の判断に基づき必要な医療機関の受診をお願いいたします。
                </p>
              </div>
            </div>
            <div className="mt-8 flex items-center justify-start right">
              <CheckBox
                id="term-and-policy"
                checked={agree}
                onChange={(e) => {
                  setAgree(e.target.checked)
                }}
              ></CheckBox>
              <label
                className="text-lg font-black checkbox"
                htmlFor="term-and-policy"
              >
                注意事項の内容を了承のうえ、
                <a
                  href="https://medicalnote-qa.jp/term_of_service"
                  target="_blank"
                  rel="noreferrer"
                  className="text-link-color underline"
                >
                  利用規約
                </a>
                ・
                <a
                  href="https://medicalnote-qa.jp/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-link-color underline"
                >
                  プライバシーポリシー
                </a>
                に同意する
              </label>
            </div>
            <div className="flex justify-center mt-3 mx-auto">
              <Button
                className="w-11/12 focus:outline-none focus:ring focus:border-blue-300 bg-link-color hover:bg-link-color"
                disabled={!submittable}
                onClick={() => setConfirm(true)}
              >
                確認する
              </Button>
            </div>
          </div>
        </div>
      )}
      {isLoggedIn && confirm && (
        <Confirm
          input={input}
          setModal={setModal}
          setConfirm={setConfirm}
          selectedSymptomIds={selectedSymptomIds}
        />
      )}
      <Modal
        title="投稿後は編集、削除できません"
        isOpen={modal}
        onRequestClose={() => setModal(false)}
      >
        <div className="mt-4 text-lg text-bold">
          一度投稿した内容は編集・削除することができなくなります。
          <br />
          投稿後は、先生の回答があるまで追加投稿できませんので、よく確認をしてから投稿をしてください。
        </div>
        <div className="mt-4 flex justify-center w-full">
          <Button
            className="w-full focus:outline-none focus:ring focus:border-blue-300 bg-link-color hover:bg-link-color"
            onClick={submit}
          >
            投稿する
          </Button>
        </div>
        <div className="flex justify-center w-full">
          <button
            className="mt-3 focus:outline-none focus:ring focus:border-blue-300 text-xl text-link-color border-blue-500 underline"
            onClick={() => setModal(false)}
          >
            キャンセル
          </button>
        </div>
      </Modal>
    </React.Fragment>
  )
}
